import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAPIHeaders } from '../../../../common/Utils';
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import PreLoader from "../../../../core/common/preLoader";
import { StudyMaterial, StudyMaterialFilter } from "../../../../types/dataModel";
import StudyMaterialList from "./studyMaterialList";
import StudyMaterialForm from "./studyMaterialForm";
import CustomToast from "../../../../core/common/customToast";
import { useAccess } from "../../../../hooks/accessContext";

interface Props {
  syllabusId: number;
  data?: any;
  refresh: () => void;
}

const sortByDisplayOrder = (items: StudyMaterial[]) =>
  [...items].sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity));

const buildDisplayOrderPayload = (
  viewContext: 'SYLLABUS' | 'UNIT' | 'SECTION',
  modalData: { id: number },
  syllabusId: number,
  reorderedItems: StudyMaterial[]
) => {
  const items = reorderedItems.map((m, idx) => ({
    id: m.id,
    displayOrder: idx + 1,
  }));

  if (viewContext === 'SYLLABUS') {
    return { syllabusId, items };
  }
  if (viewContext === 'UNIT') {
    return { unitId: modalData.id, items };
  }
  return { sectionId: modalData.id, items };
};

const StudyMaterialModal = ({ syllabusId, data, refresh }: Props) => {
  const { hasAcademicMgntEdit } = useAccess();
  const [modalData, setModalData] = useState<any>(data);
  const [studyMaterials, setStudyMaterials] = useState<StudyMaterial[]>([]);
  const [activeTab, setActiveTab] = useState<'list' | 'add'>('list');
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
  const [filter, setFilter] = useState<StudyMaterialFilter>({
    contentType: 'all',
    visibility: 'all',
    searchQuery: ''
  });
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
      show: false,
      message: "Something went wrong please try again later",
      heading: "Oops! Something went wrong",
      type: "ERROR",
  });
  const { isLoading, callFetch } = useFetch();

  useEffect(() => {
    setModalData(data);
    if (data) {
      fetchStudyMaterials(data);
    } else {
      setStudyMaterials([]);
    }
  }, [data]);

  // Ensure modal is properly initialized
  useEffect(() => {
    // Initialize Bootstrap modal if needed
    if (typeof window !== 'undefined' && (window as any).bootstrap) {
      const modalElement = document.getElementById('study_material_modal');
      if (modalElement && !modalElement.classList.contains('initialized')) {
        modalElement.classList.add('initialized');
      }
    }
  }, []);

  const getApiEndpoint = (nodeData: any) => {
    if (!nodeData) return null;
    
    if (nodeData.nodeType === 'SYLLABUS') {
      return API.GET_SYLLABUS_STUDY_MATERIALS.replace(':syllabusId', syllabusId?.toString());
    } else if (nodeData.nodeType === 'UNIT') {
      return API.GET_UNIT_STUDY_MATERIALS.replace(':unitId', nodeData.id);
    } else if (nodeData.nodeType === 'SECTION') {
      return API.GET_SECTION_STUDY_MATERIALS.replace(':sectionId', nodeData.id);
    }
    return null;
  };

  const fetchStudyMaterials = async (nodeData: any) => {
    const endpoint = getApiEndpoint(nodeData);
    if (!endpoint) return;

    try {
      const response = await callFetch(
        endpoint,
        'GET',
        getAPIHeaders(),
        null,
        null
      );
      if (Array.isArray(response)) {
        setStudyMaterials(sortByDisplayOrder(response));
      }
    } catch (err: any) {
      setToastSettings({
        show: true,
        message: err?.response?.data || "Failed to fetch study materials",
        heading: "Error",
        type: "ERROR",
      });
    }
  };

  const handleMaterialAdded = () => {
    fetchStudyMaterials(modalData);
    setActiveTab('list');
  };

  const handleMaterialDeleted = async (material: StudyMaterial) => {
    // Delete thumbnail first if it exists
    if (material.metadata?.thumbnail_file_id) {
      const thumbnailFileId = material.metadata.thumbnail_file_id;
      try {
        await callFetch(
          API.FILES.replace(':fileId', thumbnailFileId.toString()),
          'DELETE',
          getAPIHeaders(),
          null,
          null
        );
      } catch (error:any) {
        console.log('error while deleting',error);
        setToastSettings({
          show: true,
          message: error?.response?.data || "Failed to delete study material",
          heading: "Error",
          type: "ERROR",
      });
      }
    }

    // Delete the study material
    try {
      await callFetch(
        API.DELETE_STUDY_MATERIAL.replace(':id', material.id.toString()),
        'DELETE',
        getAPIHeaders(),
        null,
        null
      );
    } catch (error:any) {
      console.log('error while deleting',error);
      setToastSettings({
        show: true,
        message: error?.response?.data || "Failed to delete study material",
        heading: "Error",
        type: "ERROR",
      });
    }
    fetchStudyMaterials(modalData);
  };

  const handleEditMaterial = (material: StudyMaterial) => {
    setSelectedMaterial(material);
    setActiveTab('add');
  };

  const handleReorder = async (reorderedMaterials: StudyMaterial[]) => {
    if (!modalData?.nodeType) return;

    const updatedWithOrder = reorderedMaterials.map((m, idx) => ({
      ...m,
      displayOrder: idx + 1,
    }));

    const updatedIdMap = new Map(updatedWithOrder.map((m) => [m.id, m]));
    const merged = studyMaterials.map((m) => updatedIdMap.get(m.id) ?? m);

    const payload = buildDisplayOrderPayload(
      modalData.nodeType,
      modalData,
      syllabusId,
      updatedWithOrder
    );

    try {
      await callFetch(
        API.UPDATE_STUDY_MATERIAL_DISPLAY_ORDER,
        'PUT',
        getAPIHeaders(),
        null,
        payload
      );
      setStudyMaterials(merged);
    } catch (err: any) {
      setToastSettings({
        show: true,
        message: err?.response?.data || "Failed to update display order",
        heading: "Error",
        type: "ERROR",
      });
      fetchStudyMaterials(modalData);
    }
  };

  const renderHeader = () => (
    <>
      <div>
        <h4 className="mb-1 text-white">Study Materials</h4>
        <p className="mb-0 text-white-50 fs-12">
          {modalData?.nodeType}: {modalData?.title}
        </p>
      </div>
      <div className="d-flex align-items-center">
        <Link
          to="#"
          className="custom-btn-close d-flex align-items-center justify-content-center text-white"
          data-bs-dismiss="offcanvas"
        >
          <i className="ti ti-x" />
        </Link>
      </div>
    </>
  );

  const renderTabs = () => (
    <div className="d-flex border-bottom">
      <div className="d-flex justify-content-center align-items-center mb-2 w-100">
        <button
          className={`btn btn-sm ${activeTab === 'list' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
          onClick={() => setActiveTab('list')}
        >
          <i className="ti ti-list me-1" />
          Materials
        </button>
        <button
          className={`btn btn-sm ${activeTab === 'add' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => {
            setSelectedMaterial(null);
            setActiveTab('add');
          }}
        >
          <i className="ti ti-plus me-1" />
          Add Material
        </button>
      </div>
    </div>
  );

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
            setToastSettings((prev) => ({
                ...prev,
                show: false,
            }));
        }}
      />
      {/* Study Material Modal */}
      <div 
        className="sidebar-themesettings offcanvas offcanvas-end" 
        id="study_material_modal"
        tabIndex={-1}
        aria-labelledby="study_material_modal_label"
        aria-hidden="true"
      >
        <div className="offcanvas-header d-flex align-items-center justify-content-between bg-dark">
          {renderHeader()}
        </div>
        <div className="offcanvas-body p-0">
          {modalData ? (
            <>
              <div className="preview-content">
                {renderTabs()}
                <div className="p-3">
                  {activeTab === 'list' ? (
                    <StudyMaterialList
                      materials={studyMaterials}
                      viewContext={modalData.nodeType}
                      canReorder={hasAcademicMgntEdit()}
                      isReordering={isLoading}
                      onReorder={handleReorder}
                      onEdit={handleEditMaterial}
                      onDelete={handleMaterialDeleted}
                      filter={filter}
                      onFilterChange={setFilter}
                    />
                  ) : (
                    <StudyMaterialForm
                      syllabusId={syllabusId}
                      studyMaterial={selectedMaterial}
                      modalData={modalData}
                      onSuccess={handleMaterialAdded}
                      onCancel={() => {
                        setActiveTab('list');
                        setSelectedMaterial(null);
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="p-3 text-center">
              <p className="text-muted">No data available</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StudyMaterialModal;
